import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { SelectOption } from 'src/components/primitives/Select/hooks/types';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import Select from 'src/components/primitives/Select/Select';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import { BigNum } from '@drift-labs/sdk';

const PERIOD_OPTIONS: SelectOption<PortfolioPeriod>[] = [
	{
		label: '24H',
		value: '24h',
	},
	{
		label: '7D',
		value: '7d',
	},
	{
		label: '30D',
		value: '30d',
	},
	{
		label: '90D',
		value: '90d',
	},
];

interface Props {
	period: PortfolioPeriod;
	setPeriod: (period: PortfolioPeriod) => void;
	accountValueChange: BigNum | undefined;
	accountValueChangePct: BigNum | undefined;
	isLoading: boolean;
	isPlaceholderData: boolean;
}

export default function PortfolioHeaderBalanceChange({
	period,
	setPeriod,
	accountValueChange,
	accountValueChangePct,
	isLoading,
	isPlaceholderData,
}: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const {
		selectOptions,
		selectedOption,
		value,
		onValueChange,
		open,
		onOpenChange,
	} = useSelect({
		options: PERIOD_OPTIONS,
		selectedValue: period,
		onSelectedValueChange: setPeriod,
	});

	return (
		<div className="flex gap-xs sm:flex-col">
			<Select.Root
				open={open}
				onOpenChange={onOpenChange}
				value={value}
				onValueChange={onValueChange}
				disabled={isLoading}
			>
				<Select.Trigger
					className="w-23 border-0 bg-transparent p-0 hover:bg-transparent"
					valueClassName="typo-b4 text-text-secondary hover:text-text-default"
					disabled={isLoading}
				>
					{selectedOption?.label} Change
				</Select.Trigger>
				<Select.Options
					className="w-33"
					sideOffset={!isMobileScreenSize ? 24 : undefined}
				>
					{selectOptions.map((option) => (
						<Select.Option key={option.value} value={option.value}>
							{option.label}
						</Select.Option>
					))}
				</Select.Options>
			</Select.Root>
			<div
				className={cj(
					// `text-text-secondary` is applied when account values are hidden.
					'flex items-center gap-x-xs text-text-secondary',
					isPlaceholderData && 'animate-pulse'
				)}
			>
				<NumberDisplay
					value={accountValueChange}
					type="currency_signed"
					className="typo-b3 whitespace-nowrap"
					isAccountValueToHide
					skeletonClassName="h-[18px] w-20"
					loading={isLoading}
					signDependentColor
				/>
				<NumberDisplay
					value={accountValueChangePct}
					type="percentage_signed"
					surround="parens"
					className="typo-b3 whitespace-nowrap"
					signDependentColor
					isAccountValueToHide
					skeletonClassName="h-[18px] w-16"
					loading={isLoading}
				/>
			</div>
		</div>
	);
}
